import React, { useState } from 'react'
import { HelpCircle, ChevronDown } from 'lucide-react'

export default function FAQSection({ 
  title = "Ofte stillede spørgsmål", 
  subtitle = "Få svar på de mest almindelige spørgsmål herunder.", 
  items = [],
  className = "",
  bg = "bg-white"
}) {
  // First item open by default for engaging UX
  const [openIndices, setOpenIndices] = useState([0])

  const toggleIndex = (index) => {
    setOpenIndices(prev => 
      prev.includes(index) 
        ? prev.filter(i => i !== index) 
        : [...prev, index]
    )
  }

  if (!items || items.length === 0) return null

  // Generate FAQ schema for rich Google results
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": items.map(item => ({
      "@type": "Question",
      "name": item.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": typeof item.answer === 'string' ? item.answer : item.question
      }
    }))
  }

  return (
    <section className={`py-16 sm:py-20 border-b border-stone-200 ${bg} ${className}`}>
      {/* Schema.org FAQ data */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
      />

      <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-orange-100 text-orange-600 mb-4">
            <HelpCircle className="h-6 w-6" />
          </div>
          <h2 className="text-3xl sm:text-4xl font-bold text-stone-900 tracking-tight">
            {title}
          </h2>
          {subtitle && (
            <p className="mt-3 text-stone-600 text-base sm:text-lg max-w-2xl mx-auto">
              {subtitle}
            </p>
          )}
        </div>

        <div className="space-y-4">
          {items.map((item, index) => {
            const isOpen = openIndices.includes(index)
            return (
              <div 
                key={index} 
                className="border border-stone-200 bg-white transition-colors duration-200 hover:border-stone-300"
              >
                <button
                  type="button"
                  onClick={() => toggleIndex(index)}
                  className="w-full text-left p-6 flex items-center justify-between gap-4 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2"
                  aria-expanded={isOpen}
                >
                  <span className="font-bold text-stone-900 text-base sm:text-lg leading-snug">
                    {item.question}
                  </span>
                  <div className={`shrink-0 w-8 h-8 rounded-full bg-stone-100 flex items-center justify-center text-stone-600 transition-transform duration-200 ${isOpen ? 'rotate-180 bg-orange-100 text-orange-600' : ''}`}>
                    <ChevronDown className="h-4 w-4" />
                  </div>
                </button>

                {isOpen && (
                  <div className="px-6 pb-6 pt-1 border-t border-stone-100">
                    <div className="text-stone-600 text-sm sm:text-base leading-relaxed">
                      {item.answer}
                    </div>
                  </div>
                )}
              </div>
            )
          })}
        </div>
      </div>
    </section>
  )
}
